import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useOtpStore } from '@/store/otpStore';
import { OtpFailedToSendError, OtpTooManyAttemptsError } from './otp-errors';

/** Canonical header the UI uses to carry the OTP code. Backend reads `x-otp-code`. */
export const OTP_HEADER = 'X-Otp-Code';

const OTP_RETRY_FLAG = '__otpRetry__';

type OtpKind = 'required' | 'invalid' | 'tooMany' | 'failedToSend';

interface ErrorBody {
  name?: string;
  message?: string;
}

interface WrappedStatusBody {
  data?: { status?: string };
}

function classifyName(name: string | undefined): OtpKind | null {
  switch (name) {
    case 'OtpRequiredError':
      return 'required';
    case 'InvalidOtpError':
      return 'invalid';
    case 'TooManyOtpAttemptsError':
      return 'tooMany';
    case 'FailedToSendOtpError':
      return 'failedToSend';
    default:
      return null;
  }
}

function classifyStatus(status: string | undefined): OtpKind | null {
  switch (status) {
    case 'otpRequired':
      return 'required';
    case 'invalidOtp':
      return 'invalid';
    case 'tooManyOtpAttempts':
      return 'tooMany';
    case 'failedToSendOtp':
    case 'error':
      return 'failedToSend';
    default:
      return null;
  }
}

function classifyResponse(response: AxiosResponse | undefined): OtpKind | null {
  if (!response) return null;
  const errorKind = classifyName((response.data as ErrorBody | undefined)?.name);
  if (errorKind) return errorKind;
  return classifyStatus((response.data as WrappedStatusBody | undefined)?.data?.status);
}

function isOtpRetry(config: AxiosRequestConfig | undefined): boolean {
  return Boolean(config && (config as Record<string, unknown>)[OTP_RETRY_FLAG]);
}

function withRetryFlag(config: AxiosRequestConfig, code: string): AxiosRequestConfig {
  return {
    ...config,
    headers: { ...config.headers, [OTP_HEADER]: code },
    [OTP_RETRY_FLAG]: true,
  } as AxiosRequestConfig;
}

/**
 * Runs the prompt → retry loop until either:
 * - the request succeeds (resolves with the final response),
 * - the user cancels (rejects with OtpCancelledError from the store),
 * - the backend reports too-many or send failure (rejects with the typed error).
 *
 * The retry call is marked with `OTP_RETRY_FLAG` so the same interceptor
 * passes its response/error through to this loop instead of recursing.
 */
async function runOtpLoop(originalConfig: AxiosRequestConfig, instance: AxiosInstance): Promise<AxiosResponse> {
  const store = useOtpStore.getState;
  while (true) {
    const code = await store().requestOtp(); // throws on cancel
    const retryConfig = withRetryFlag(originalConfig, code);

    let response: AxiosResponse;
    try {
      response = await instance.request(retryConfig);
    } catch (err) {
      const axiosErr = err as AxiosError;
      const kind = classifyResponse(axiosErr.response);
      if (kind === 'invalid') {
        store().markInvalid();
        continue;
      }
      if (kind === 'tooMany') {
        store().failTooManyAttempts();
        throw new OtpTooManyAttemptsError();
      }
      if (kind === 'failedToSend') {
        store().closeOtp();
        throw new OtpFailedToSendError();
      }
      // Non-OTP error during retry — bubble to caller with the form closed.
      store().closeOtp();
      throw err;
    }

    const kind = classifyResponse(response);
    if (kind === 'invalid') {
      store().markInvalid();
      continue;
    }
    if (kind === 'tooMany') {
      store().failTooManyAttempts();
      throw new OtpTooManyAttemptsError();
    }
    if (kind === 'failedToSend') {
      store().closeOtp();
      throw new OtpFailedToSendError();
    }
    if (kind === 'required') {
      // Backend re-prompted even after OTP was sent — treat as invalid to re-loop.
      store().markInvalid();
      continue;
    }
    store().closeOtp();
    return response;
  }
}

/**
 * Install the OTP response interceptor on the given axios instance.
 * Returns a teardown function that removes the interceptor — useful for
 * tests and HMR.
 *
 * Detects both backend transports:
 * - `nextWithError` mode: HTTP 4xx with body `{ name: 'OtpRequiredError' | … }`
 * - `respondWithStatus` mode: HTTP 200 with body `{ data: { status: 'otpRequired' | … } }`
 *
 * Retries the original request with `X-Otp-Code` once the user submits.
 * Loops on invalid codes, bubbles cancel/too-many/failed errors.
 */
export function installOtpInterceptor(instance: AxiosInstance): () => void {
  const handlerId = instance.interceptors.response.use(
    (response) => {
      if (isOtpRetry(response.config)) return response;
      const kind = classifyResponse(response);
      if (kind === 'required' || kind === 'invalid') {
        return runOtpLoop(response.config as AxiosRequestConfig, instance);
      }
      if (kind === 'tooMany') {
        useOtpStore.getState().failTooManyAttempts();
        throw new OtpTooManyAttemptsError();
      }
      if (kind === 'failedToSend') {
        throw new OtpFailedToSendError();
      }
      return response;
    },
    (error: AxiosError) => {
      if (isOtpRetry(error.config)) throw error;
      const kind = classifyResponse(error.response);
      if (kind === 'required' || kind === 'invalid') {
        return runOtpLoop(error.config as AxiosRequestConfig, instance);
      }
      if (kind === 'tooMany') {
        useOtpStore.getState().failTooManyAttempts();
        throw new OtpTooManyAttemptsError();
      }
      if (kind === 'failedToSend') {
        throw new OtpFailedToSendError();
      }
      throw error;
    },
  );

  return () => {
    instance.interceptors.response.eject(handlerId);
  };
}
